/* eslint-disable consistent-return */
import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useUserStore } from '../store';

const routes: Array<RouteRecordRaw> = [
	{
		path: '/',
		name: 'home',
		component: () =>
			import(/* webpackChunkName: "HomePage" */ '@apps/ops/src/views/homePage/HomePage.vue'),
		meta: {
			title: 'Home',
		},
	},
	{
		path: '/division',
		name: 'division',
		component: () =>
			import(/* webpackChunkName: "division" */ '@shared/components/division/Division.vue'),
		meta: {
			title: 'Division',
		},
	},
	{
		path: '/balance-transfers',
		name: 'balance-transfers',
		component: () =>
			import(
				/* webpackChunkName: "balance-transfers" */ '@shared/components/balanceTransfer/balanceTransfers.vue'
			),
		meta: {
			title: 'Balance Transfers',
		},
	},
	{
		path: '/bank-integration',
		name: 'bankIntegration',
		component: () =>
			import(
				/* webpackChunkName: "BankIntegration" */ '@apps/ops/src/views/pspIntegration/BankIntegration.vue'
			),
		meta: {
			title: 'Bank Integration',
		},
	},
	{
		path: '/configurations',
		redirect: '/configurations/payouts',
		name: 'configurations',
		component: () =>
			import(
				/* webpackChunkName: "Configurations" */ '@shared/components/configuration/Configurations.vue'
			),
		meta: {
			title: 'Configurations',
		},
		children: [
			{
				path: 'kyc',
				component: () =>
					import(
						/* webpackChunkName: "KycConfiguration" */ '@shared/components/configuration/KycConfiguration.vue'
					),
			},
			{
				path: 'payouts',
				component: () =>
					import(
						/* webpackChunkName: "PayoutsConfiguration" */ '@shared/components/configuration/PayoutsConfiguration.vue'
					),
				children: [
					{
						path: 'scheduler/:type',
						component: () =>
							import(
								/* webpackChunkName: "PayoutSchedulerConfiguration" */ '@shared/components/configuration/PayoutSchedulerConfiguration.vue'
							),
					},
				],
			},
			{
				path: 'balanceTransfers',
				component: () =>
					import(
						/* webpackChunkName: "BalanceTransfersConfiguration" */ '@shared/components/configuration/BalanceTransfersConfiguration.vue'
					),
			},
			{
				path: 'webhooks',
				component: () =>
					import(
						/* webpackChunkName: "WebhooksConfiguration" */ '@shared/components/configuration/WebhooksConfiguration.vue'
					),
			},
			{
				path: 'hosted-onboarding',
				component: () =>
					import(
						/* webpackChunkName: "HostedOnboardingConfiguration" */ '@shared/components/configuration/HostedOnboardingConfiguration.vue'
					),
			},
			{
				path: 'payment-providers',
				component: () =>
					import(
						/* webpackChunkName: "PspsConfiguration" */ '@shared/components/configuration/PspsConfiguration.vue'
					),
			},
		],
	},
	{
		path: '/login',
		name: 'login',
		component: () =>
			import(/* webpackChunkName: "LoginPage" */ '@apps/ops/src/views/LoginPage.vue'),
		meta: {
			title: 'Login',
		},
	},
	{
		path: '/payouts',
		name: 'payouts',
		component: () =>
			import(
				/* webpackChunkName: "PayoutsOps" */ '@apps/ops/src/views/payoutsOps/PayoutsPageOps.vue'
			),
		meta: {
			title: 'Payouts',
		},
		children: [
			{
				path: 'division-requests',
				name: 'division-requests',
				component: () =>
					import(
						/* webpackChunkName: "DivisionRequests" */ '@apps/ops/src/views/payoutsOps/PayoutsPageOps.vue'
					),
				meta: {
					title: 'Division Requests',
				},
			},
			{
				path: 'requests',
				name: 'payout-requests',
				component: () =>
					import(
						/* webpackChunkName: "PayoutRequests" */ '@apps/ops/src/views/payoutsOps/PayoutsPageOps.vue'
					),
				meta: {
					title: 'Payout Requests',
				},
			},
		],
	},
	{
		path: '/payouts/requests/:payoutRequestCode',
		name: 'payout-request-details',
		component: () =>
			import(
				/* webpackChunkName: "PayoutRequestDetails" */ '@shared/views/PayoutRequestDetailsPage.vue'
			),
		meta: {
			title: 'Payout Requests Details',
		},
	},
	{
		path: '/psp-integration',
		name: 'pspIntegration',
		component: () =>
			import(/* webpackChunkName: "PspIntegration" */ '@shared/views/PspIntegration.vue'),
		meta: {
			title: 'PSP Integration',
		},
	},
	{
		path: '/kycChecks',
		name: 'kycChecks',
		component: () =>
			import(
				/* webpackChunkName: "KycChecks" */ '@apps/ops/src/views/kycChecks/KycChecks.vue'
			),
		meta: {
			title: 'KYC Checks',
		},
	},
	{
		path: '/reports',
		redirect: '/reports/division',
		name: 'reports',
		component: () =>
			import(/* webpackChunkName: "reportsOps" */ '@shared/views/reports/Reports.vue'),
		meta: {
			title: 'reports',
		},
		children: [
			{
				path: 'division',
				component: () => import('@shared/views/reports/DivisionReports.vue'),
				props: true,
			},
			{
				path: 'sellers',
				component: () => import('@shared/views/reports/SellerReports.vue'),
				props: true,
			},
		],
	},
	{
		path: '/sellers',
		name: 'sellers',
		component: () =>
			import(/* webpackChunkName: "Sellers" */ '@apps/ops/src/views/sellers/SellersOps.vue'),
		meta: {
			title: 'Sellers',
		},
	},
	{
		path: '/sellers/:sellerCode/:tab',
		name: 'sellerDetails',
		component: () =>
			import(
				/* webpackChunkName: "SellerDetails" */ '@shared/components/sellerDetails/SellerDetails.vue'
			),
		meta: {
			title: 'Seller details',
		},
	},
	{
		path: '/transactions',
		name: 'transactions',
		component: () =>
			import(/* webpackChunkName: "Transactions" */ '@shared/views/TransactionsPage.vue'),
		meta: {
			title: 'Transactions',
		},
	},
	{
		path: '/:pathMatch(.*)*',
		name: 'NotFound',
		component: () =>
			import(/* webpackChunkName: "NotFound" */ '@apps/ops/src/views/NotFound.vue'),
	},
];

const router = createRouter({
	history: createWebHistory(process.env.BASE_URL),
	routes,
});

router.beforeEach(async (to) => {
	document.title = `Ops | ${to.meta.title}`;
	const userStore = useUserStore();
	const sessionHasExpired = parseInt(localStorage.getItem('_expiredJWT') || '0', 10) < Date.now();

	if (to.name !== 'login' && (!userStore.isUserLoggedIn || sessionHasExpired)) {
		return { name: 'login' };
	}
});

export default router;
